import * as React from 'react'
import {
  IActiveTripResponseV2,
  ICoordinates,
  IMarker,
  IRiderLocationObject,
  IServiceLocationObject,
  IZoneObject
} from '../../types'
import { Circle, CircleProps, GoogleMap, Marker, Polygon, Polyline, withGoogleMap } from 'react-google-maps'
// @ts-ignore
import { IRenderConditions } from '../MapView'

import * as CarIcon from '../../components/icons/google-map/CarIcon.svg'
import * as dropOffMarker from '../../components/icons/google-map/DropOffIcon.svg'
import * as pickUpMarker from '../../components/icons/google-map/PickUpIcon.svg'
import * as locationMarker from '../../components/icons/google-map/PointIcon.svg'
import * as additionalStop from '../../components/icons/google-map/AdditionalStopIcon.svg'

interface IGoogleMapComponentProps {
  center?: ICoordinates
  circles?: CircleProps[]
  currentTrip?: IActiveTripResponseV2
  decodedPolyline: any
  destination: ICoordinates | null
  isDraggable: boolean
  isModal?: boolean
  location: IRiderLocationObject | null | IServiceLocationObject
  locationType: string | undefined
  markers?: IMarker[]
  onMapMounted: any
  origin: ICoordinates | null
  polygon?: ICoordinates[] | null
  renderStrategy: IRenderConditions
  zone?: IZoneObject | null
  zoneId?: string
  zones?: { [key: string]: IZoneObject } | null
  zoom: number
  onDestPinChanged?: (event: google.maps.MouseEvent) => void
  onOriginPinChanged?: (event: google.maps.MouseEvent) => void
  selectLocation?: (event: google.maps.MouseEvent) => void
}

const renderMarkers = (props: IGoogleMapComponentProps) => {
  const {
    markers,
    renderStrategy: { isShowAdditionalStop, isShowCars, isShowDestination, isShowOrigin }
  } = props

  if (!markers) {
    return null
  }

  return markers.map((m, index) => {
    if (m.type === 'origin' && isShowOrigin) {
      return (
        <Marker
          draggable={false}
          icon={{ url: pickUpMarker.toString() }}
          key={`marker-${index}`}
          position={m}
          title={m.title}
          onDragEnd={props.onOriginPinChanged}
        />
      )
    } else if (m.type === 'destination' && isShowDestination) {
      return (
        <Marker
          draggable={false}
          icon={{ url: dropOffMarker.toString() }}
          key={`marker-${index}`}
          onDragEnd={props.onDestPinChanged}
          position={m}
          title={m.title}
        />
      )
    } else if (m.type === 'car' && isShowCars) {
      return (
        <Marker
          draggable={false}
          icon={{ url: CarIcon.toString() }}
          key={`markerWithLabel-${index}`}
          label={{ text: m.title }}
          labelAnchor={new google.maps.Point(0, 0)}
          position={m}
          title={m.title}
        />
      )
    } else if (m.type === 'additionalStop' && isShowAdditionalStop) {
      return (
        <Marker
          draggable={false}
          icon={{ url: additionalStop.toString() }}
          key={`marker-${index}`}
          position={m}
          title={m.title}
        />
      )
    } else {
      return null
    }
  })
}

const GoogleMapComponent = withGoogleMap((props: IGoogleMapComponentProps) => {
  const {
    center,
    circles,
    currentTrip,
    decodedPolyline,
    destination,
    isDraggable,
    isModal,
    location,
    locationType,
    onMapMounted,
    origin,
    renderStrategy,
    zone,
    zones,
    zoom,
    selectLocation
  } = props

  const icon = {
    ADDITIONALSTOP: additionalStop,
    DROPOFF: dropOffMarker,
    PICKUP: pickUpMarker
  }

  const locationIcon =
    locationType && icon[locationType as keyof typeof icon] ? icon[locationType as keyof typeof icon] : locationMarker

  const zonePath = props.zone
    ? props.zone.shape
      ? props.zone.shape.coordinates[0][0].map(ps => ({ lat: ps[1], lng: ps[0] }))
      : []
    : []

  const zonePaths: Array<{
    detail: { color: string; visible: boolean | undefined }
    coordinates:
      | google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>
      | google.maps.MVCArray<google.maps.LatLng>
      | Array<Array<google.maps.LatLng | google.maps.LatLngLiteral>>
      | Array<google.maps.LatLng | google.maps.LatLngLiteral>
  }> = []

  const zoneCircle: Array<{
    detail: { color: string; visible: boolean | undefined }
    cordinate: { center: { lat: number; lng: number }; radius: number }
  }> = []

  const showPolygonMapServiceZones = (zone: IZoneObject) => {
    if (!currentTrip && (zone.shapeType === 'shape_polygon' || zone.shapeType === 'shape_multipolygon')) {
      return true
    }
    if (
      currentTrip &&
      (zone.shapeType === 'shape_polygon' || zone.shapeType === 'shape_multipolygon') &&
      zone.serviceId === currentTrip.service.serviceId
    ) {
      return true
    }
    return false
  }

  const showCircleMapSeriviceZones = (zone: IZoneObject) => {
    if (!currentTrip && zone.shapeType === 'shape_circle') {
      return true
    }
    if (currentTrip && zone.shapeType === 'shape_circle' && zone.serviceId === currentTrip.service.serviceId) {
      return true
    }
    return false
  }

  if (zones) {
    Object.keys(zones).forEach(element => {
      if (zones && zones[element]) {
        const zone = zones[element]
        if (showPolygonMapServiceZones(zone)) {
          const coordinates = zone ? (zone.shape ? zone.shape.coordinates[0] : []) : []
          const paths = coordinates.map(coord => coord.map(ps => ({ lat: ps[1], lng: ps[0] })))
          zonePaths.push({ detail: { color: zone.color, visible: zone.visible }, coordinates: paths })
        }

        if (showCircleMapSeriviceZones(zone)) {
          zoneCircle.push({
            detail: { color: zone.color, visible: zone.visible },
            cordinate: {
              center: {
                lat: zone.center ? zone.center.coordinates[0] : 0,
                lng: zone.center ? zone.center.coordinates[1] : 0
              },
              radius: zone.radius || 0
            }
          })
        }
      }
    })
  }

  return (
    <GoogleMap
      defaultCenter={center}
      mapTypeId={isModal ? 'satellite' : 'roadmap'}
      options={{
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: ['roadmap', 'satellite']
        },
        streetViewControl: false
      }}
      ref={onMapMounted}
      zoom={isModal ? 18 : zoom}
      // onBoundsChanged={props.onBoundsChanged}
    >
      {renderMarkers(props)}
      {location && (!origin && !destination) && (
        <Marker
          draggable={isDraggable}
          icon={{ url: locationIcon.toString() }}
          position={location.coordinates}
          onDragEnd={selectLocation}
        />
      )}

      {/* {props.origin && props.markers && props.renderStrategy.isShowCircle && ( */}
      {/* <Circle
        center={{ lat: 14.4826861345524, lng: 121.046092191968 }}
        // radius={getCircleToFitPoints(props.origin, props.markers, 400, 50000)}
        radius={3000.56905782734}
        options={{
          // strokeColor: '#38B44F',
          strokeColor: '#a200ff',
          strokeOpacity: 0.5,
          strokeWeight: 2,
          fillOpacity: 0.1,
          // fillColor: '#74ED75'
          fillColor: '#7823c7',
          
        }}
      /> */}

      {/* // )} */}

      {currentTrip &&
        zoneCircle.map(circle => (
          <Circle
            center={{ lat: circle.cordinate.center.lng, lng: circle.cordinate.center.lat }}
            options={{
              fillColor: circle.detail.color,
              fillOpacity: 0.3,
              strokeColor: circle.detail.color,
              strokeOpacity: 0.5,
              strokeWeight: 2
            }}
            radius={circle.cordinate.radius}
          />
        ))}

      {currentTrip &&
        zonePaths.map(zonePath => (
          <Polygon
            paths={zonePath.coordinates}
            options={{
              fillColor: zonePath.detail.color,
              fillOpacity: 0.3,
              strokeColor: zonePath.detail.color,
              strokeOpacity: 0.5,
              strokeWeight: 2
            }}
          />
        ))}

      {circles &&
        circles.length &&
        circles.map((circleProps: CircleProps, idx) => <Circle {...circleProps} key={idx} />)}
      {decodedPolyline && renderStrategy.isShowPolyline && (
        <Polyline
          options={{
            strokeColor: '#38B44F',
            strokeOpacity: 0.7,
            strokeWeight: 3
          }}
          path={decodedPolyline}
        />
      )}
      {zone && (
        <Polygon
          options={{
            fillOpacity: 0.1,
            fillColor: zone ? zone.color : '',
            strokeOpacity: 0.5,
            strokeWeight: 2
          }}
          path={zonePath}
        />
      )}

      {zonePaths.map(
        (name, index) =>
          name.detail.visible && (
            <Polygon
              key={`map-zone-polygon-${index}`}
              options={{
                fillOpacity: 0.35,
                fillColor: name.detail.color,
                strokeOpacity: 0.5,
                strokeWeight: 2
              }}
              paths={name.coordinates}
            />
          )
      )}

      {zoneCircle.map(
        (name, index) =>
          name.detail.visible && (
            <Circle
              center={{ lat: name.cordinate.center.lng, lng: name.cordinate.center.lat }}
              key={`map-zone-circle-${index}`}
              options={{
                fillOpacity: 0.3,
                fillColor: name.detail.color,
                strokeColor: name.detail.color,
                strokeOpacity: 0.5,
                strokeWeight: 2
              }}
              radius={name.cordinate.radius}
            />
          )
      )}
    </GoogleMap>
  )
})

export default GoogleMapComponent



// WEBPACK FOOTER //
// ./src/components/google-map/GoogleMap.tsx